<template>
    <div class="tariff-group text-primary">
        <div class="tariff-group__details">
            <div class="tariff-group__details-text">
                <h4 class="mb-3 mt-1" v-text="'Wybierz pakiet'" />

                <p v-text="'Pakiet obejmuje:'" class="font-weight-bold mb-1" />
                <ul class="list-unstyled mb-4">
                    <li
                        v-for="(feature, fIdx) of $options.TARIFF.features.list"
                        class="tariff-group__feature mt-2"
                        :key="fIdx"
                    >
                        <a-icon icon="Ok" width="16" />
                        <div>
                            {{ feature.label }}
                            <a-icon
                                v-if="feature.tooltip"
                                v-tooltip="feature.tooltip"
                                icon="Info"
                                width="16"
                                height="16"
                                class="text-primary"
                                style="position: relative; bottom: -2px;"
                            />
                        </div>
                    </li>
                </ul>

                <hr />

                <div class="tariff-group__row d-flex align-items-center justify-content-between w-100 mt-4">
                    <h6 class="mb-1" v-text="'Opcjonalnie (stała kwota za dowolną liczbę urządzeń w ramach limitu):'" />
                </div>
                <ul class="pl-3 mb-1">
                    <li class="mt-2"
                        v-for="(feature, fIdx) of $options.TARIFF.optionals.list"
                        :key="fIdx"
                        v-text="feature.name"
                    />
                </ul>
            </div>

            <ui-slider class="tariff-group__slider"
                :options="{
                    items: groupWithTariffInfo.tariffs.length > 1 ? 2 : 1,
                    gutter: 16,
                    slideBy: 1,
                    loop: false,
                    responsive: {
                        0: { items: 1 },
                        576: { items: groupWithTariffInfo.tariffs.length > 1 ? 2 : 1 }
                    }
                }"
            >
                <div
                    v-for="tariff of groupWithTariffInfo.tariffs"
                    :key="tariff.id"
                    class="tariff-group__slider-row"
                >
                    <drs-tariff
                        :key="tariff.id"
                        :tariff="tariff"
                        :extremes="tariffExtremes"
                        class="tariff-group__tariff"
                    />
                </div>
            </ui-slider>
        </div>
        <p
            v-if="groupWithTariffInfo.tariffs.length > 1 && tariffExtremes.cheapest"
            class="tariff-group__discount-note mb-0 mt-2"
        >
            *W porównaniu do pakietu Specjalnego EC opłacanego w cyklu miesięcznym<br>
            W pakiecie rocznym opłata za 12 miesięcy jest pobierana z góry. Łączną kwotę poznasz po konfiguracji.
        </p>
        <p
            class="tariff-group__discount-note mb-0 mt-0"
        >
            Wszystkie podane kwoty to miesięczne stawki netto dla jednego sklepu.
        </p>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DrsTariff from '@/components/drs/DrsTariff.vue'
import { TARIFF } from '@/utils/constants/drs/tariffs'

export default {
    TARIFF,

    props: {
        group: {
            type: Object,
            required: true
        }
    },

    methods: {
        ...mapActions('drsTariffs', ['loadDsTariffs', 'loadDevices', 'loadTariffConfigFromStorage']),

        getTariffAnnualizedCost(tariff) {
            if (!tariff) {
                return undefined
            }

            const hasValidDiscount = typeof tariff.discounted_price === 'number' && tariff.discounted_price > 0
            if (!hasValidDiscount) {
                // If there is no valid discounted price, return the annual cost based on the regular price
                return tariff.price * 12
            }

            // Calculate the number of months the discounted price is valid for, ensuring it is between 0 and 12
            const discountedMonths = Math.min(
                Math.max(this.$moment(tariff.discounted_price_date_to).diff(this.$moment(), 'months'), 0),
                12
            )

            return {
                annualPrice:
                    discountedMonths * (tariff.discounted_price) + (12 - discountedMonths) * tariff.price,
                annualPriceDevices:
                    discountedMonths * (tariff.devices_discounted_price || 0) +
                    (12 - discountedMonths) * (tariff.devices_price || 0)
            }
        }
    },

    computed: {
        ...mapGetters('drsTariffs', ['selectedTariff']),

        groupWithTariffInfo() {
            let tariffs = Array.isArray(this.group?.tariffs) ? this.group.tariffs : []

            if (!tariffs.length || tariffs.length === 1) {
                return this.group
            }

            tariffs = tariffs.map(tariff => {
                const { annualPrice, annualPriceDevices } = this.getTariffAnnualizedCost(tariff)
                return {
                    ...tariff,
                    annualPrice,
                    annualPriceDevices
                }
            })

            return {
                ...this.group,
                tariffs
            }
        },

        // Get the extremes of the tariffs in the group (cheapest and most expensive)
        tariffExtremes() {
            const tariffs = this.groupWithTariffInfo?.tariffs || []
            if (tariffs.length < 2) {
                return { cheapest: null, mostExpensive: null }
            }

            const sortedTariffs = [...tariffs].sort((a, b) => {
                const priceA = a.annualPrice || a.price
                const priceB = b.annualPrice || b.price
                return priceA - priceB
            })

            return {
                cheapest: sortedTariffs[0],
                mostExpensive: sortedTariffs[sortedTariffs.length - 1]
            }
        }
    },

    components: {
        DrsTariff
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.tariff-group {
    display: flex;
    align-items: center;
    flex-direction: column;

    &__details {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    &__details-text {
        margin-top: 72px;
    }

    &__row {
        > :nth-child(2) {
            width: 13.5rem;
        }
    }

    &__feature {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        .a-icon {
            min-width: 16px;
            margin-right: 0.25rem;
            color: var(--color-success);
        }
    }

    &__tariff {
        height: 100%;
        max-width: 300px;
        margin-left: auto;
        margin-right: auto;
    }

    &__slider-row {
        display: flex;
        padding-top: 2rem;
        height: 100%;
    }

    &__slider {
        width: 550px;
        max-width: 100%;

        .tns-slider {
            display: flex;
        }

        .tns-item {
            height: auto;
        }
    }

    &__discount-note {
        text-align: right;
        font-size: 0.875rem;
        font-style: italic;
        align-self: flex-end;
    }

    @include media-breakpoint-down(md) {
        &__details {
            flex-wrap: wrap;
        }

        &__details-text {
            margin-top: 0;
        }

        &__discount-note {
            text-align: center;
        }

        &__slider {
            max-width: 100%;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
        }
    }
}
</style>